import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAdminCompanies } from "../../api/admin";
import { useDeleteFirmware, useFirmwareList, type FirmwareSearchField } from "../../api/firmware";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { DataTable } from "../../components/DataTable";
import { StatusBadge } from "../../components/StatusBadge";
import { extractErrorMessage } from "../../api/errors";
import { getActingCompanyName, getActingTenantId, getRole, startActingAsCompany, stopActingAsCompany } from "../../auth/session";
import type { Firmware } from "../../api/types";
import { Pencil, Rocket, Trash2 } from "lucide-react";

const statusOptions = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "deprecated", label: "Deprecated" },
];

const searchFields = [
  { value: "filename", label: "Name" },
  { value: "version", label: "Version" },
];

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

export function FirmwareListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const deleteFirmware = useDeleteFirmware();
  const isSuperAdmin = getRole() === "super_admin";
  const companiesQuery = useAdminCompanies({ enabled: isSuperAdmin });
  const companies = companiesQuery.data?.data ?? [];
  const [scopeTenantId, setScopeTenantId] = useState(getActingTenantId() ?? "");
  const [scopeCompanyName, setScopeCompanyName] = useState(getActingCompanyName() ?? "");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState<FirmwareSearchField>("filename");
  // Cursors for pages after the first, so Previous steps back one page.
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const page = cursors.length + 1;
  const debouncedSearch = useDebouncedValue(search, 300);
  const [deleteTarget, setDeleteTarget] = useState<Firmware | null>(null);
  const [deleteError, setDeleteError] = useState("");

  // Search and status are applied by the server across every page.
  const { data, isLoading, isPlaceholderData } = useFirmwareList({
    status: statusFilter || undefined,
    cursor,
    search: debouncedSearch,
    searchField,
  });
  const rows = data?.data ?? [];
  const hasMore = !!data?.pagination.hasMore;

  function showNextPage() {
    const next = data?.pagination.cursor;
    if (!next || isPlaceholderData) return;
    setCursors([...cursors, next]);
  }
  const selectedCompanyName = scopeCompanyName || companies.find((company) => company.tenantId === scopeTenantId)?.companyName || "";
  const scopeLabel = scopeTenantId ? selectedCompanyName || "Selected company files" : "Platform admin files";

  function resetList() {
    setCursors([]);
    queryClient.invalidateQueries({ queryKey: ["firmware"] });
  }

  function selectPlatformScope() {
    stopActingAsCompany();
    setScopeTenantId("");
    setScopeCompanyName("");
    resetList();
  }

  function selectCompanyScope(tenantId: string) {
    if (!tenantId) {
      selectPlatformScope();
      return;
    }

    const company = companies.find((candidate) => candidate.tenantId === tenantId);
    startActingAsCompany(tenantId, company?.companyName);
    setScopeTenantId(tenantId);
    setScopeCompanyName(company?.companyName ?? "");
    resetList();
  }

  async function handleDelete() {
    if (!deleteTarget) return;

    setDeleteError("");
    try {
      await deleteFirmware.mutateAsync(deleteTarget.firmwareId);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(extractErrorMessage(err, "Failed to delete file"));
    }
  }

  const columns = [
    { key: "filename", header: "Name" },
    { key: "releaseNotes", header: "Description", render: (row: Firmware) => row.releaseNotes || <span className="text-gray-400 dark:text-riot-navy-400">-</span> },
    {
      key: "status",
      header: "File type",
      render: (row: Firmware) => <StatusBadge status={row.status} />,
    },
    { key: "fileSize", header: "Size", render: (row: Firmware) => formatSize(row.fileSize) },
    { key: "model", header: "Model" },
    {
      key: "createdAt",
      header: "Last used at",
      render: (row: Firmware) => new Date(row.createdAt).toLocaleString(),
    },
    {
      key: "actions",
      header: "",
      width: "112px",
      render: (row: Firmware) => (
        <div className="flex justify-end gap-1">
          <button
            type="button"
            onClick={() => navigate(`/tasks/create?firmwareId=${encodeURIComponent(row.firmwareId)}`)}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-riot-navy-700 dark:hover:text-riot-gold-400"
            aria-label={`Create rollout task for ${row.filename}`}
            title="Create rollout task"
          >
            <Rocket size={14} />
          </button>
          <button
            type="button"
            onClick={() => navigate(`/firmware/${row.firmwareId}/edit`)}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-riot-navy-700 dark:hover:text-riot-gold-400"
            aria-label={`Edit ${row.filename}`}
            title="Edit"
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={() => {
              setDeleteTarget(row);
              setDeleteError("");
            }}
            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-300"
            aria-label={`Delete ${row.filename}`}
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {isSuperAdmin && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-riot-navy-700 dark:bg-riot-navy-800">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-riot-gold-500">File scope</p>
              <h2 className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">{scopeLabel}</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-riot-navy-300">
                Uploads, edits, deletes, and rollout tasks apply to the selected file scope.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={selectPlatformScope}
                className={`rounded-md border px-3 py-2 text-sm font-medium ${
                  !scopeTenantId
                    ? "border-riot-gold-400 bg-riot-gold-400 text-riot-navy-900"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"
                }`}
              >
                Platform files
              </button>
              <select
                value={scopeTenantId}
                onChange={(event) => selectCompanyScope(event.target.value)}
                disabled={companiesQuery.isLoading}
                className="min-w-[260px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                aria-label="Choose company file scope"
              >
                <option value="">{companiesQuery.isLoading ? "Loading companies..." : "Choose company files..."}</option>
                {companies.map((company) => (
                  <option key={company.tenantId} value={company.tenantId}>
                    {company.companyName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="py-20 text-center text-sm text-gray-400 dark:text-riot-navy-400">Loading files...</div>
      ) : (
        <DataTable
          title="Files"
          columns={columns}
          data={rows}
          keyExtractor={(f) => f.firmwareId}
          selectable
          serverSide
          emptyMessage={
            hasMore && rows.length === 0 && !isPlaceholderData ? (
              <span>
                No matches in the files checked so far.{" "}
                <button type="button" onClick={showNextPage} className="font-medium text-riot-gold-600 hover:text-riot-gold-700 dark:text-riot-gold-400">
                  Keep searching
                </button>
              </span>
            ) : undefined
          }
          filters={{
            searchPlaceholder: "Search by",
            searchFields,
            statusOptions,
          }}
          onFilterChange={(f) => {
            setStatusFilter(f.status);
            setSearch(f.search);
            setSearchField((f.searchField as FirmwareSearchField) || "filename");
            setCursors([]);
          }}
          pagination={{
            page,
            count: rows.length,
            hasMore,
            onNext: showNextPage,
            onPrev: () => setCursors(cursors.slice(0, -1)),
            onFirst: () => setCursors([]),
          }}
          actions={
            <button
              onClick={() => navigate("/firmware/upload")}
              className="rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600"
            >
              {isSuperAdmin ? "Upload to scope" : "Upload"}
            </button>
          }
        />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true" aria-label="Delete file confirmation">
          <div className="w-full max-w-md rounded-lg border border-riot-navy-700 bg-white p-5 shadow-xl dark:bg-riot-navy-900">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Delete file?</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-riot-navy-200">
              {deleteTarget.filename} will be marked inactive and removed from file lists.
            </p>

            {deleteError && <p className="mt-3 text-sm text-red-600 dark:text-red-300">{deleteError}</p>}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteFirmware.isPending}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 active:bg-red-800 disabled:opacity-50"
              >
                {deleteFirmware.isPending ? "Deleting..." : "Delete file"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
